import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ListPanelContext } from '@/components/listPanelContext';

interface ListToolbarProps {
    search?: ReactNode;
    controls?: ReactNode;
    title?: ReactNode;
    trailing?: ReactNode;
    footer?: ReactNode;
    boxed?: boolean;
    panel?: ReactNode;
}

export const ListToolbar = ({ search, controls, title, trailing, footer, panel, boxed = false }: ListToolbarProps) => {
    const [target, setTarget] = useState<HTMLDivElement | null>(null);
    const active = useRef<{ id: string; close: () => void } | null>(null);
    const activate = useCallback((id: string, close: () => void): (() => void) => {
        if(active.current?.id !== id){ active.current?.close(); }
        active.current = { id, close };
        return () => { if(active.current?.id === id){ active.current = null; } };
    }, []);
    const context = useMemo(() => ({ target, activate }), [target, activate]);
    return (
        <ListPanelContext.Provider value={context}>
            <div
                className={`flex w-full min-w-0 scroll-mt-20 flex-col ${boxed ? 'rounded-lg border border-slate-200/80 bg-box p-4 dark:border-white/16' : ''}`}
            >
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    {title && <h2 className="text-base font-bold text-slate-900 dark:text-white">{title}</h2>}
                    {search && <div className="min-w-0 basis-full xl:basis-64 xl:flex-1">{search}</div>}
                    {controls && <div className="flex w-full max-w-full flex-wrap items-center gap-2 xl:w-auto [&>div]:w-full xl:[&>div]:w-auto">{controls}</div>}
                    {trailing && <div className="ml-auto flex flex-wrap items-center gap-2">{trailing}</div>}
                </div>
                {footer}
                <div ref={setTarget} className="empty:hidden" />
                {panel}
            </div>
        </ListPanelContext.Provider>
    );
};
